import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { IUser } from "@eagerdog/interfaces";

import { helperService } from "../../services/helper.service";

import Breadcrumb from "src/components/Breadcrumb/Breadcrumb";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import SectionSidebar, { ISectionDefinition, SectionMenuButton, sectionsFromTabs } from "src/components/SectionSidebar/SectionSidebar";

import BulkDiscountCodesTab from "./BulkDiscountCodesTab/BulkDiscountCodesTab";
import CompanyMetricsTab from "./CompanyMetricsTab/CompanyMetricsTab";
import TrustedSecretariesTab from "./TrustedSecretariesTab/TrustedSecretariesTab";
import ResetPasswordTab from "./ResetPasswordTab/ResetPasswordTab";

import styles from "./Admin.module.scss";

interface IBulkDiscountMetrics {
  totalCodes: number;
  usedCodes: number;
  availableCodes: number;
  usageRate: number;
}

const ADMIN_SECTIONS: ISectionDefinition[] = [
  { name: "Bulk Discount Codes", group: "Billing", icon: "discounts" },
  { name: "Company Metrics", group: "Insights", icon: "results" },
  { name: "Trusted Secretaries", group: "Users", icon: "registrations" },
  { name: "Reset Password", group: "Users", icon: "user" },
];

const Admin: React.FC = () => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [metrics, setMetrics] = useState<IBulkDiscountMetrics>();
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const user: IUser = helperService.getUser();
  const { activeTab, setTab } = useTabContent();

  const tabs: ITab[] = [
    { name: "Bulk Discount Codes", actions: [] },
    { name: "Company Metrics", actions: [] },
    { name: "Trusted Secretaries", actions: [] },
    { name: "Reset Password", actions: [] },
  ];

  const loadMetrics = async () => {
    try {
      // Import the apiService here since it may not be needed in other parts
      const { apiService } = await import("../../services/api.service");
      const analytics = await apiService.getBulkDiscountAnalytics();
      
      setMetrics({
        totalCodes: analytics.total_codes || 0,
        usedCodes: analytics.used_codes || 0,
        availableCodes: analytics.available_codes || 0,
        usageRate: Number(((analytics.usage_rate || 0) * 100).toFixed(2))
      });
    } catch (error) {
      console.error("Failed to load metrics:", error);
      // Set to zeros if loading fails
      setMetrics({
        totalCodes: 0,
        usedCodes: 0,
        availableCodes: 0,
        usageRate: 0.00
      });
    }
  };

  useEffect(() => {
    if (!loaded) {
      // Check if user is system admin
      if (user.user_type !== 'eagerdog_admin') {
        // Redirect or show error
        window.location.href = '/';
        return;
      }

      loadMetrics();
      setLoaded(true);
    }
  }, [loaded, user]);

  const pageLabel = tabs[activeTab]?.name || "Admin Dashboard";

  return (
    <div className={styles.Admin}>
      <Helmet>
        <title>Admin Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <SectionSidebar
        storageKey="eagerdog.adminSidebarCollapsed"
        menuLabel="Admin menu"
        eyebrow="Eager Dog"
        header={{
          title: "Admin Dashboard",
          subtitle: user?.display_name,
          fallbackName: "Eager Dog",
        }}
        groups={sectionsFromTabs(tabs, ADMIN_SECTIONS)}
        activeId={String(activeTab)}
        onSelect={(id) => setTab(Number(id))}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className={styles.main}>
        <div className={styles.inner}>
          <div className={styles.pageHeader}>
            <Breadcrumb crumbs={[{ label: "Admin Dashboard" }, { label: pageLabel }]} />
            <SectionMenuButton label="Admin menu" onClick={() => setMobileNavOpen(true)} />
          </div>
          <h1>{pageLabel}</h1>

          {activeTab === 0 && metrics && (
            <div className={styles.metrics}>
              <div className={styles.metricItem}>
                <div className={styles.num}>{metrics.totalCodes}</div>
                <div className={styles.label}>Total Codes</div>
              </div>
              <div className={styles.metricItem}>
                <div className={styles.num}>{metrics.usedCodes}</div>
                <div className={styles.label}>Used Codes</div>
              </div>
              <div className={styles.metricItem}>
                <div className={styles.num}>{metrics.availableCodes}</div>
                <div className={styles.label}>Available Codes</div>
              </div>
              <div className={styles.metricItem}>
                <div className={styles.num}>{metrics.usageRate}%</div>
                <div className={styles.label}>Usage Rate</div>
              </div>
            </div>
          )}

          <TabContent hideTabs setTab={setTab} activeTab={activeTab} tabs={tabs}>
            <BulkDiscountCodesTab currentTab={activeTab} tabIndex={0} />
            <CompanyMetricsTab currentTab={activeTab} tabIndex={1} />
            <TrustedSecretariesTab currentTab={activeTab} tabIndex={2} />
            <ResetPasswordTab currentTab={activeTab} tabIndex={3} />
          </TabContent>
        </div>
      </div>
    </div>
  );
};

export default Admin;
